import * as React from 'react';
import { FC, memo, useCallback } from 'react';

import { Badge, Box, Icon } from '@rbilabs/universal-components';

import Picture from 'components/picture';
import { RemoveRewardButton } from 'pages/loyalty/loyalty-rewards/remove-reward-button';
import { t } from 'state/intl/translations';
import { LoyaltyReward } from 'state/loyalty/types';
import { primitive } from 'styles/constants/primitives';
import { TraceName, stopTrace } from 'utils/performance/trace';

import { ILoyaltyRewardsViewProps } from '../../loyalty-rewards/types';
import { incentiveCardImageDefaultProps } from '../constants';

import {
  AddIconButton,
  AlertIcon,
  BackgroundImage,
  CardContainer,
  ImageWrapper,
  InfoContainer,
  InlineAlertContainer,
  InlineAlertSpacer,
  InlineAlertText,
  RewardText,
  StyledClickableContainer,
  StyledRewardLock,
} from './incentive-card.styled';
import { parseIncentiveData } from './parse-incentive-data';

export interface IIncentiveCard {
  incentive: LoyaltyReward;
  handlePress: ILoyaltyRewardsViewProps['handleOnPress'];
  locked?: boolean;
  inlineAlertMessage?: React.ReactNode;
  isPictureBlurhashEnabled?: boolean;
  isApplied?: boolean;
  inCooldown?: boolean;
}

export const IncentiveCard: FC<React.PropsWithChildren<IIncentiveCard>> = memo(
  ({
    incentive,
    handlePress,
    locked = false,
    inlineAlertMessage,
    isPictureBlurhashEnabled = true,
    isApplied = false,
    inCooldown = false,
  }) => {
    const incentiveData = parseIncentiveData(incentive);

    const { image, imageDescription, name, backgroundImage } = incentiveData;

    const onDisplay = useCallback(() => stopTrace(TraceName.REWARDS_LOAD), []);

    return (
      <CardContainer>
        <StyledClickableContainer
          isApplied={isApplied}
          isLocked={locked || !!inlineAlertMessage}
          onPress={() => (!locked && !isApplied && !inCooldown ? handlePress(incentive) : null)}
        >
          {isApplied && (
            <Box position="absolute" right="$1" top="$1" zIndex={1}>
              <Badge
                variant="default-subtle"
                iconName="checkFill"
                backgroundColor={primitive.bk.$successLight}
                _text={{ fontSize: '2xs' }}
                iconSize="3"
                paddingLeft="1"
                paddingRight="1"
              >
                {t('applied', 'Applied')}
              </Badge>
            </Box>
          )}
          <InfoContainer>
            {image ? (
              <ImageWrapper>
                {backgroundImage && <BackgroundImage alt={name} image={backgroundImage} />}
                <Picture
                  isBlurhashEnabled={isPictureBlurhashEnabled}
                  image={image}
                  alt={imageDescription}
                  onDisplay={onDisplay}
                  {...incentiveCardImageDefaultProps}
                />
              </ImageWrapper>
            ) : null}
            <RewardText>{name}</RewardText>
          </InfoContainer>
          {locked ? (
            <StyledRewardLock accessibilityLabel={t('locked')} />
          ) : (
            <>
              {inlineAlertMessage ? (
                <>
                  <InlineAlertSpacer></InlineAlertSpacer>
                  <InlineAlertContainer>
                    <AlertIcon />
                    <InlineAlertText>{inlineAlertMessage}</InlineAlertText>
                  </InlineAlertContainer>
                </>
              ) : (
                <>
                  {isApplied ? (
                    <RemoveRewardButton />
                  ) : (
                    <AddIconButton
                      variant="outline"
                      onPress={() => handlePress(incentive)}
                      icon={<Icon variant="add" />}
                      disabled={locked}
                      testID="add-to-cart"
                      accessibilityLabel={t('addToCart')}
                    />
                  )}
                </>
              )}
            </>
          )}
        </StyledClickableContainer>
      </CardContainer>
    );
  }
);
